import { React, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Card,
	Row,
	Col,
	Button,
	Spin,
	message,
	Typography,
	Space,
	Input,
	DatePicker,
	Select,
	Form,
} from "antd";
import {
	ArrowLeftOutlined,
	UserOutlined,
	CalendarOutlined,
	PhoneOutlined,
	HomeOutlined,
	TeamOutlined,
	FileTextOutlined,
	SaveOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import SearchMemberModal from "./SearchMemberModal";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AddMember = () => {
	const isMobile = useIsMobile();
	const navigate = useNavigate();
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(false);
	const [form] = Form.useForm();
	const [statusTypes, setStatusTypes] = useState([]);
	const [classificationTypes, setClassificationTypes] = useState([]);
	const [baptismalStatusTypes, setBaptismalStatusTypes] = useState([]);
	const [searchMemberModalVisible, setSearchMemberModalVisible] =
		useState(false);

	// Fetch dropdown data on component mount
	useEffect(() => {
		fetchDropdownData();
	}, []);

	const fetchDropdownData = async () => {
		try {
			setLoading(true);

			// Fetch status types
			const statusResponse = await useAxios.get(
				"/users/statuses/"
			);
			if (statusResponse.status === 200) {
				setStatusTypes(statusResponse.data);
			}

			// Fetch classification types
			const classificationResponse = await useAxios.get(
				"/users/classifications/"
			);
			if (classificationResponse.status === 200) {
				setClassificationTypes(
					classificationResponse.data
				);
			}

			// Fetch baptismal status types
			const baptismalResponse = await useAxios.get(
				"/users/baptismal-statuses/"
			);
			if (baptismalResponse.status === 200) {
				setBaptismalStatusTypes(baptismalResponse.data);
			}
		} catch (error) {
			message.error(
				"드롭다운 데이터를 불러오는데 실패했습니다."
			);
		} finally {
			setLoading(false);
		}
	};

	const handleBack = () => {
		navigate("../view-all-members");
	};

	const handleMemberSelect = (member) => {
		form.setFieldsValue({
			led_by_id: member.id,
			led_by_name: member.name,
		});
	};

	const formatPhoneNumber = (phoneNumber) => {
		if (!phoneNumber) return phoneNumber;

		// Remove all non-digit characters
		const digits = phoneNumber.replace(/\D/g, "");

		// Format based on length
		if (digits.length === 10) {
			// 0101234567 -> 010-1234-567
			return digits.replace(
				/(\d{3})(\d{4})(\d{3})/,
				"$1-$2-$3"
			);
		} else if (digits.length === 11) {
			// 01012345678 -> 010-1234-5678
			return digits.replace(
				/(\d{3})(\d{4})(\d{4})/,
				"$1-$2-$3"
			);
		}

		// If no specific format matches, return as is
		return phoneNumber;
	};

	const handleSave = async () => {
		// 이미 저장 중이면 중복 호출 방지
		if (saving) {
			return;
		}

		try {
			setSaving(true);
			const values = await form.validateFields();

			const memberData = {
				...values,
				birthdate: values.birthdate
					? values.birthdate.format("YYYY-MM-DD")
					: null,
				phone_number: formatPhoneNumber(
					values.phone_number
				),
				date_of_registration:
					values.date_of_registration
						? values.date_of_registration.format(
								"YYYY-MM-DD"
						  )
						: null,
			};

			const response = await useAxios.post(
				`/users/add-member/`,
				memberData
			);

			if (response.status === 201) {
				message.success(
					"성도가 성공적으로 추가되었습니다."
				);
				console.log(
					"response.data: ",
					response.data.user_id
				);
				// 2단계로 이동 (소속 정보 및 이명 기록 추가)
				navigate(
					`../add-member-step2/${response.data.user_id}`
				);
			}
		} catch (error) {
			message.error("성도 추가에 실패했습니다.");
		} finally {
			setSaving(false);
		}
	};

	const EditableInfoItem = ({
		label,
		name,
		icon,
		type = "input",
		options = [],
	}) => (
		<div className="member-detail__info-item">
			<Text className="member-detail__info-label">
				{label}:
			</Text>
			<div className="member-detail__info-value">
				{icon && (
					<span className="member-detail__info-icon">
						{icon}
					</span>
				)}
				<Form.Item
					name={name}
					style={{ margin: 0, flex: 1 }}>
					{type === "select" ? (
						<Select
							placeholder={`${label} 선택`}>
							{options.map(
								(option) => (
									<Option
										key={
											option.id
										}
										value={
											option.id
										}>
										{
											option.name
										}
									</Option>
								)
							)}
						</Select>
					) : type === "date" ? (
						<DatePicker
							inputReadOnly={isMobile}
							placeholder={`${label} 선택`}
							style={{
								width: "100%",
							}}
							format="YYYY-MM-DD"
						/>
					) : type === "textarea" ? (
						<TextArea
							placeholder={`${label} 입력`}
							rows={3}
						/>
					) : (
						<Input
							placeholder={`${label} 입력`}
						/>
					)}
				</Form.Item>
			</div>
		</div>
	);

	if (loading) {
		return (
			<div className="member-detail__loading">
				<Spin size="large" />
				<Text>데이터를 불러오는 중...</Text>
			</div>
		);
	}

	return (
		<>
			<PageTitle title="성도 추가" />
			<div className="member-detail__container">
				<div className="member-detail__header">
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={handleBack}
						className="member-detail__back-button">
						목록으로 돌아가기
					</Button>
					<Title
						level={2}
						className="member-detail__title">
						성도 추가
					</Title>
					<Button
						icon={<SaveOutlined />}
						onClick={handleSave}
						loading={saving}
						disabled={saving}
						type="primary"
						className="member-detail__edit-button">
						저장
					</Button>
				</div>

				<Form form={form} layout="vertical">
					<div className="member-detail__content">
						{/* 기본 정보 섹션 */}
						<Card
							title={
								<Space>
									<UserOutlined />
									기본
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="성명"
										name="name"
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="성별"
										name="gender"
										type="select"
										options={[
											{
												id: "남",
												name: "남",
											},
											{
												id: "여",
												name: "여",
											},
										]}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="생년월일"
										name="birthdate"
										type="date"
										icon={
											<CalendarOutlined />
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="연락처"
										name="phone_number"
										icon={
											<PhoneOutlined />
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="직업"
										name="job"
									/>
								</Col>
							</Row>
							<Row
								gutter={[
									24, 16,
								]}
								style={{
									marginTop: 16,
								}}>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div className="member-detail__info-item">
										<Text className="member-detail__info-label">
											인도자
											ID:
										</Text>
										<div className="member-detail__info-value">
											<Form.Item
												name="led_by_id"
												style={{
													margin: 0,
													flex: 1,
												}}>
												<Input
													placeholder="인도자 ID 입력"
													readOnly
												/>
											</Form.Item>
											{form.getFieldValue(
												"led_by_id"
											) && (
												<Button
													type="primary"
													size="small"
													icon={
														<UserOutlined />
													}
													onClick={() =>
														navigate(
															`../view-member-detail/${form.getFieldValue(
																"led_by_id"
															)}`
														)
													}
													style={{
														marginLeft: 8,
													}}
													title="인도자 상세정보 보기">
													이동
												</Button>
											)}
										</div>
									</div>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div className="member-detail__info-item">
										<Text className="member-detail__info-label">
											인도자:
										</Text>
										<div className="member-detail__info-value">
											<Form.Item
												name="led_by_name"
												style={{
													margin: 0,
													flex: 1,
												}}>
												<Input placeholder="인도자 이름 입력" />
											</Form.Item>
											<Button
												type="primary"
												size="small"
												icon={
													<SearchOutlined />
												}
												onClick={() =>
													setSearchMemberModalVisible(
														true
													)
												}
												style={{
													marginLeft: 8,
												}}>
												조회
											</Button>
										</div>
									</div>
								</Col>
							</Row>
						</Card>

						{/* 주소 정보 섹션 */}
						<Card
							title={
								<Space>
									<HomeOutlined />
									주소
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								<Col
									xs={24}
									sm={12}>
									<EditableInfoItem
										label="주소"
										name="address"
									/>
								</Col>
								<Col
									xs={24}
									sm={12}>
									<EditableInfoItem
										label="상세주소"
										name="address_detail"
									/>
								</Col>
							</Row>
						</Card>

						{/* 교회 정보 섹션 */}
						<Card
							title={
								<Space>
									<TeamOutlined />
									교회
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="분류"
										name="classification"
										type="select"
										options={
											classificationTypes
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="상태"
										name="status"
										type="select"
										options={
											statusTypes
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="등록일"
										name="date_of_registration"
										type="date"
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="세례상태"
										name="baptismal_status"
										type="select"
										options={
											baptismalStatusTypes
										}
									/>
								</Col>
							</Row>
						</Card>

						{/* 기타 정보 섹션 */}
						<Card
							title={
								<Space>
									<FileTextOutlined />
									기타
									정보
								</Space>
							}
							className="member-detail__card">
							<Row>
								<Col span={24}>
									<EditableInfoItem
										label="메모"
										name="etc"
										type="textarea"
									/>
								</Col>
							</Row>
						</Card>
					</div>
				</Form>
			</div>

			{/* Search Member Modal */}
			<SearchMemberModal
				visible={searchMemberModalVisible}
				onCancel={() =>
					setSearchMemberModalVisible(false)
				}
				onSelect={handleMemberSelect}
			/>
		</>
	);
};

export default AddMember;
